import React, { useState } from 'react';
import AdminList, { TemplateNameCellRenderer } from '../../ui/admin/AdminList';
import { useQuery } from '@apollo/react-hooks';
import { Template, IdAndName } from '../../../shared/types';
import { createStyles, makeStyles } from '@material-ui/core';
import { ColDef } from 'ag-grid-community';
import AdminListToolbar from '../../ui/admin/AdminListToolbar';
import { filterArrayOfObjectsBy, sortArray } from '../../../utils/sorting.utils';
import gql from 'graphql-tag';
import { premiumChannelsEnabled, topicsOrCategories } from '../../../utils/utils';

const name = 'Templates';

interface TemplatesTreeRowItem {
	_id: string;
	title: string;
	categoriesHierarchy: string[];
	categories?: IdAndName[];
	tags?: IdAndName[];
	publishedBy: string | boolean;
	channel?: IdAndName;
}

const TEMPLATES = gql`
query getTemplates {
	templates {
		_id
		title
		categories { _id name }
		tags { _id name }
		publishedBy { _id name }
		channel { _id name }
	}
}`;


const useStyles = makeStyles((theme) => createStyles({
	gridHeader: {
		fontWeight: 'bold'
	},
	toolbarWrapper: {
		'& > div > div': {
			marginLeft: 0
		}
	},
	title: {
		display: 'flex',
		alignItems: 'center',
		height: '100%',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'normal !important' as 'normal',
		overflowWrap: 'break-word !important' as 'break-word'
	},
	categories: {
		display: 'flex',
		alignItems: 'center',
		whiteSpace: 'normal',
		wordWrap: 'break-word',
		lineHeight: theme.typography.body2.lineHeight
	}
}));

const transformTemplateToTree = (templates: Template[]): TemplatesTreeRowItem[] => {
	const treeRowData: TemplatesTreeRowItem[] = [];
	templates.forEach((template: Template) => {
		if (!treeRowData.some(x => x._id === template.publishedBy._id)) {
			treeRowData.push({
				_id: template.publishedBy._id,
				title: template.publishedBy.name,
				categoriesHierarchy: [template.publishedBy.name],
				publishedBy: false
			});
		}
		treeRowData.push({
			_id: template._id,
			title: template.title,
			categoriesHierarchy: [template.publishedBy.name, template.title],
			categories: template.categories,
			tags: template.tags,
			publishedBy: template.publishedBy._id,
			channel: template.channel
		});
	});
	return treeRowData.sort((a, b) => a.title.localeCompare(b.title));
};

const autoGroupColumnDef: ColDef = {
	menuTabs: [],
	sortable: false,
	headerName: 'Template name',
	minWidth: 504,
	cellRendererParams: {
		suppressCount: true,
		innerRendererFramework: (props: any) => <TemplateNameCellRenderer wrap={false} addFromTemplate {...props} />
	}
}

const ArticleAddFromTemplate = (): JSX.Element => {
	const classes = useStyles();
	const columnDefs: ColDef[] = [
		{
			colId: 'category-articles-col',
			headerName: topicsOrCategories(true, true),
			headerClass: classes.gridHeader,
			field: 'categories',
			sortable: false,
			width: 240,
			autoHeight: true,
			cellRendererFramework: (props: any) => <div className={classes.categories}>{props.value?.map((tag: any) => tag.name).join(', ')}</div>,
			menuTabs: []
		},
		{
			headerName: 'Tags',
			field: 'tags',
			width: 240,
			sortable: false,
			valueFormatter: (props: any) => props.data.tags?.map((tag: any) => tag.name).join(', '),
			menuTabs: []
		},
		...(premiumChannelsEnabled(false) ? [		{
			headerName: 'Channel',
			field: 'channel',
			width: 240,
			sortable: false,
			valueFormatter: (props: any) => props.data.channel?.name,
			menuTabs: []
		}] : [])
	];

	const [searchString, setSearchString] = useState('');
	const queryTemplates = useQuery(TEMPLATES, {fetchPolicy: 'network-only'});
	const templates = (queryTemplates?.data?.templates as Template[]) ?? [];

	if (queryTemplates.loading) return <></>;

	const treeRowData = transformTemplateToTree(sortArray(filterArrayOfObjectsBy(templates, searchString, ['title']), 'title'));
	const templatesNumberWithoutDesks = treeRowData.filter((item) => item.publishedBy).length;

	return (
		<AdminList
			title="Choose a template to create a new article"
			name={name}
			items={treeRowData}
			itemsNumber={templatesNumberWithoutDesks}
			loading={queryTemplates.loading}
			error={queryTemplates.error}
			columns={columnDefs}
			autoGroupColumn={autoGroupColumnDef}
			showAdd={false}
			treeData={true}
			entityForCaptionText={templatesNumberWithoutDesks > 1 ? 'Templates' : 'Template'}
			toolbar={
				<div className={classes.toolbarWrapper}>
					<AdminListToolbar
						searchString={searchString}
						toolbarPlaceholder="Search templates..."
						onQueryStringChange={setSearchString}
					/>
				</div>
			}
		/>
	);
};

export default ArticleAddFromTemplate;
